/**
 * TusCourses PWA - Main Entry Point
 *
 * Application bootstrap with:
 * - Vue 3 with Composition API
 * - Pinia State Management
 * - Vue Router
 * - PrimeVue UI Framework
 * - PWA Service Worker
 */

import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'

// PrimeVue
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import { TusWannseePreset } from './assets/styles/theme/primevue-preset'

// PrimeIcons
import 'primeicons/primeicons.css'

// Styles
import './assets/styles/main.css'

// Create Vue App
const app = createApp(App)

// Pinia Store
const pinia = createPinia()
app.use(pinia)

// Vue Router
app.use(router)

// PrimeVue with Custom Theme
app.use(PrimeVue, {
  // PrimeUI Community License — ohne gueltigen Schluessel blendet PrimeVue 5 ein
  // sichtbares Banner in die UI ein. Kein Secret (steht im Client-Bundle), daher
  // Build-Variable statt Secret-Store. Typ siehe src/vite-env.d.ts.
  license: import.meta.env.VITE_PRIMEUI_LICENSE,
  theme: {
    preset: TusWannseePreset,
    options: {
      darkModeSelector: '.dark',
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue, components, utilities',
      },
    },
  },
  ripple: true,
  locale: {
    // German locale
    startsWith: 'Beginnt mit',
    contains: 'Enthält',
    notContains: 'Enthält nicht',
    endsWith: 'Endet mit',
    equals: 'Gleich',
    notEquals: 'Nicht gleich',
    noFilter: 'Kein Filter',
    lt: 'Kleiner als',
    lte: 'Kleiner oder gleich',
    gt: 'Größer als',
    gte: 'Größer oder gleich',
    dateIs: 'Datum ist',
    dateIsNot: 'Datum ist nicht',
    dateBefore: 'Datum vor',
    dateAfter: 'Datum nach',
    clear: 'Löschen',
    apply: 'Anwenden',
    matchAll: 'Alle übereinstimmen',
    matchAny: 'Beliebige übereinstimmen',
    addRule: 'Regel hinzufügen',
    removeRule: 'Regel entfernen',
    accept: 'Ja',
    reject: 'Nein',
    choose: 'Auswählen',
    upload: 'Hochladen',
    cancel: 'Abbrechen',
    dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
    dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    monthNames: [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ],
    monthNamesShort: [
      'Jan',
      'Feb',
      'Mär',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dez',
    ],
    today: 'Heute',
    weekHeader: 'KW',
    firstDayOfWeek: 1, // Monday
    dateFormat: 'dd.mm.yy',
    weak: 'Schwach',
    medium: 'Mittel',
    strong: 'Stark',
    passwordPrompt: 'Passwort eingeben',
    emptyFilterMessage: 'Keine Ergebnisse gefunden',
    emptyMessage: 'Keine Optionen verfügbar',
    aria: {
      trueLabel: 'Wahr',
      falseLabel: 'Falsch',
      nullLabel: 'Nicht ausgewählt',
      pageLabel: 'Seite {page}',
      firstPageLabel: 'Erste Seite',
      lastPageLabel: 'Letzte Seite',
      nextPageLabel: 'Nächste Seite',
      prevPageLabel: 'Vorherige Seite',
      rowsPerPageLabel: 'Zeilen pro Seite',
    },
  },
})

// PrimeVue Services
app.use(ToastService)
app.use(ConfirmationService)

// Mount App
app.mount('#app')

// Register Service Worker (handled by vite-plugin-pwa)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // SW registration is handled by vite-plugin-pwa
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[PWA] Service Worker support detected')
    }
  })
}
